/**
 * composition/index — **composition root ตัวจริงของแอป**
 * ประกอบ router ของจริง (browser history) · React Router v8 → `ADR-006`
 *
 * 📌 **เดิมชื่อ `router.ts` และ `deps.ts` ถูกเข้าใจผิดว่าเป็น root** — ตัวที่เป็น root
 *    จริงคือไฟล์นี้ เพราะมันเป็นคนเรียก `composeApp()` เอง และเป็นคนแก้ปัญหาลำดับ
 *    ข้างล่าง · ชื่อเดิมทำให้ไม่มีใครเดาถูก (owner ทัก 2026-08-07) ⇒ `index.*` =
 *    หน้าตาของโฟลเดอร์ ตามกฎเดียวกับ `routes/`
 *
 * 🐔 **ลำดับที่มีไก่กับไข่ และวิธีที่เลือกแก้:**
 *    `deps` ต้องมี `navigation` ⇒ `navigation` ต้องเรียก `router.navigate()`
 *    ⇒ แต่ `router` ต้องมี `deps` ก่อนถึงจะสร้าง route table ได้
 *
 *    แก้ด้วย **holder object** ที่ `navigate` อ้างผ่าน closure แล้ว resolve ตอนถูก
 *    เรียกจริง (ซึ่งเกิดหลัง router ถูกสร้างเสมอ — ผู้ใช้กดอะไรไม่ได้ก่อนหน้าจอ render)
 *    ⚠️ ใช้ holder ไม่ใช่ `let` เพราะ `prefer-const` จะฟ้อง `let` ที่ assign ครั้งเดียว
 *       (และเปลี่ยนเป็น `const` ไม่ได้จริง ๆ เพราะ closure ต้องอ้างก่อนค่าจะมี)
 */
import { createBrowserRouter } from 'react-router';
import { appRoutes } from '../routes/index.js';
import { composeApp } from './compose-deps.js';

type AppRouter = ReturnType<typeof createBrowserRouter>;

export function createAppRouter(): AppRouter {
  const holder: { current?: AppRouter } = {};

  const deps = composeApp((path) => {
    const router = holder.current;
    if (router === undefined) {
      // ⚠️ throw ไม่ใช่เงียบ — no-op เงียบ ๆ คือ "ปุ่มที่กดแล้วไม่มีอะไรเกิดขึ้น"
      //    ซึ่งไล่หาสาเหตุยากกว่า error ที่ดังทันที
      throw new Error('navigation.go() ถูกเรียกก่อน router ถูกสร้าง — ตรวจลำดับใน createAppRouter()');
    }
    /**
     * `navigate()` คืน promise ที่ reject ได้ (เช่น loader ของ route ปลายทาง throw)
     * ⇒ **ห้ามโยนทิ้ง** — ทิ้งแล้วผู้ใช้กดปุ่มแล้วอยู่หน้าเดิมโดยไม่มีอะไรบอกว่าทำไม
     * Phase 1 ยังไม่มี error boundary กลาง ⇒ อย่างน้อยต้องเห็นใน console
     */
    router.navigate(path).catch((err: unknown) => {
      console.error('เปลี่ยนหน้าไม่สำเร็จ:', path, err);
    });
  });

  holder.current = createBrowserRouter(appRoutes(deps));
  return holder.current;
}
